import { Link } from "react-router-dom"
import { Navbar } from "@/components/navbar"
import { Footer } from "@/components/footer"
import { PolicyHeader } from "@/components/shared"
import { companyDetails } from "@/lib/company-details"

const effectiveDate = "April 12, 2026"

const sections = [
  {
    id: "scope",
    title: "Scope and Relationship to Other Terms",
    content:
      "These Terms of Use govern your access to and use of Monra's website, brand materials, general support channels, and other non-product-specific Monra experiences. Some Monra offerings have additional product-specific terms. For example, use of Monra eSIM is subject to these Terms of Use, the Monra eSIM Service Terms, the Privacy Policy, and the Refund Policy where applicable. If product-specific terms conflict with these Terms of Use, the product-specific terms control for that product.",
  },
  {
    id: "contracting-party",
    title: "Contracting Party",
    content:
      `${companyDetails.legalName}, trading as Monra, is the contracting party for Monra products and services unless a specific Monra service, invoice, order confirmation, or checkout flow expressly states otherwise.`,
  },
  {
    id: "eligibility",
    title: "Eligibility and Authority",
    content:
      "You must be legally capable of entering into a binding agreement in your jurisdiction to use Monra. If you use Monra on behalf of a business, team, or another person, you represent that you have authority to bind that party to these Terms of Use and any applicable product-specific terms.",
  },
  {
    id: "acceptable-use",
    title: "Acceptable Use of the Website and Brand Properties",
    content:
      "You may use Monra only for lawful purposes and in a manner that does not harm the website, the brand, or other users.",
    bullets: [
      "Do not misuse, disrupt, scrape, overload, probe, or interfere with the Monra website, forms, communications, or related systems.",
      "Do not use Monra content, trademarks, logos, or brand materials in a misleading way or without permission.",
      "Do not submit unlawful, fraudulent, abusive, defamatory, infringing, or harmful content through Monra forms, support channels, or public-facing features.",
      "Do not attempt to gain unauthorized access to any Monra account, infrastructure, or unpublished material.",
    ],
  },
  {
    id: "accounts",
    title: "Accounts, Communications, and Submitted Information",
    content:
      "Some Monra experiences may require you to provide contact details, account information, or messages. You are responsible for the accuracy of information you provide and for activity that occurs under any account you control.",
    bullets: [
      "Provide accurate, current, and complete information when interacting with Monra.",
      "Keep your login credentials, email access, and device access secure.",
      "Promptly notify Monra if you believe your account or contact details have been compromised or used without authorization.",
      "You consent to receive service-related communications from Monra by email or through the product experience where relevant.",
    ],
  },
  {
    id: "intellectual-property",
    title: "Intellectual Property and Brand Use",
    content:
      "All rights, title, and interest in Monra's website, design, text, software, content, trademarks, logos, and other brand assets remain with Monra or its licensors. Except for the limited right to use the website and services in accordance with these Terms of Use, no license or ownership right is transferred to you.",
  },
  {
    id: "feedback",
    title: "Feedback and Submissions",
    content:
      "If you send Monra feedback, suggestions, ideas, or other submissions, you agree that Monra may use them without restriction or compensation, unless a separate written agreement states otherwise. This does not reduce any privacy commitments that apply to personal information under the Privacy Policy.",
  },
  {
    id: "third-party",
    title: "Third-Party Services and Links",
    content:
      "Monra may link to or rely on third-party websites, platforms, stores, communications providers, payment partners, or infrastructure services. Monra is not responsible for third-party content, policies, availability, or practices, and your use of third-party services may be governed by their own terms and policies.",
  },
  {
    id: "suspension",
    title: "Suspension and Termination",
    content:
      "Monra may suspend, restrict, or terminate access to the website, communications channels, or any associated account if we reasonably believe there has been misuse, fraud, unlawful conduct, security risk, or a violation of these Terms of Use or applicable product-specific terms.",
  },
  {
    id: "disclaimers",
    title: "Disclaimers",
    content:
      "To the maximum extent permitted by law, Monra is provided on an \"as is\" and \"as available\" basis. Monra disclaims all warranties not expressly stated, including implied warranties of merchantability, fitness for a particular purpose, non-infringement, availability, accuracy, or uninterrupted access. General website content is provided for informational purposes and does not constitute legal, financial, telecom, tax, or regulatory advice.",
  },
  {
    id: "liability",
    title: "Limitation of Liability",
    content:
      "To the maximum extent permitted by law, Monra and its operators, personnel, contractors, and service providers will not be liable for any indirect, incidental, consequential, special, exemplary, or punitive damages, including loss of profit, revenue, goodwill, data, or business opportunity, arising out of or related to your use of Monra. To the maximum extent permitted by law, Monra's aggregate liability arising from these Terms of Use will not exceed the greater of the amount you paid, if any, for the relevant Monra experience during the twelve months before the event giving rise to the claim or one hundred U.S. dollars (US$100).",
  },
  {
    id: "changes",
    title: "Changes to Monra and These Terms",
    content:
      "Monra may update the website, the brand presentation, these Terms of Use, or related policies from time to time. When required, we will post updated terms, revise the effective date, or provide additional notice. Your continued use of Monra after updated terms take effect means you accept the updated terms for future use.",
  },
  {
    id: "contact",
    title: "Contact Us",
    content:
      "For questions about these Terms of Use or Monra's legal documents, contact Monra support.",
    email: companyDetails.contactEmail,
  },
]

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-background animate-fade-in">
      <Navbar links={[]} logoVariant="main" logoBehavior="navigate" isTransparent={false} />

      <PolicyHeader
        title="Terms of Use"
        subtitle="General terms for using Monra's website, brand properties, and support channels."
        effectiveDate={effectiveDate}
      />

      <section className="bg-background py-12 sm:py-16">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="legal-card mb-8">
            <h2 className="mb-3 font-sans text-lg font-semibold text-foreground">Related Legal Documents</h2>
            <p className="text-sm leading-relaxed text-muted-foreground">
              Product-specific Monra offerings may have additional terms. Monra eSIM is governed by these Terms of Use plus its
              {" "}
              <Link to="/esim-service-terms" className="font-semibold text-primary transition-colors hover:underline">
                Service Terms
              </Link>
              {" "}
              and
              {" "}
              <Link to="/refund-policy" className="font-semibold text-primary transition-colors hover:underline">
                Refund Policy
              </Link>
              .
            </p>
          </div>

          <div className="legal-card mb-10">
            <h2 className="mb-4 font-sans text-lg font-semibold text-foreground">Table of Contents</h2>
            <ol className="space-y-2 text-sm text-muted-foreground">
              {sections.map((section, index) => (
                <li key={section.id} className="flex gap-3">
                  <span className="w-6 shrink-0 tabular-nums text-foreground/45">{String(index + 1).padStart(2, "0")}</span>
                  <a href={`#${section.id}`} className="transition-colors hover:text-foreground">
                    {section.title}
                  </a>
                </li>
              ))}
            </ol>
          </div>

          {sections.map((section) => (
            <section key={section.id} id={section.id} className="scroll-mt-20 border-t border-border py-10">
              <h2 className="mb-3 font-sans text-xl font-semibold tracking-tight text-foreground">{section.title}</h2>
              <p className="leading-relaxed text-muted-foreground">{section.content}</p>
              {"bullets" in section && section.bullets && (
                <ul className="mt-4 space-y-2 text-muted-foreground">
                  {section.bullets.map((bullet, index) => (
                    <li key={index} className="list-disc pl-5">
                      {bullet}
                    </li>
                  ))}
                </ul>
              )}
              {"email" in section && section.email && (
                <a
                  href={`mailto:${section.email}`}
                  className="mt-2 inline-block font-semibold text-primary transition-colors hover:underline"
                >
                  {section.email}
                </a>
              )}
            </section>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  )
}
